<script setup>
import { ref, computed, onMounted } from 'vue';
import { useWaitlistStore } from '../store/useWaitlist';
import WaitlistAddModal from '../components/WaitlistAddModal.vue';
import WaitlistScheduleModal from '../components/WaitlistScheduleModal.vue';
const store = useWaitlistStore();
onMounted(() => store.load());
const accountId = window.location.pathname.split('/')[3];
const JANELA = { manha: 'Manhã', tarde: 'Tarde', qualquer: 'Qualquer' };
const numerados = computed(() => store.items.map((it, i) => ({ ...it, posicao: i + 1 })));
const filtroProf = ref('Todos');
const filtroJanela = ref('Todas');
const profissionais = computed(() => ['Todos', ...new Set(store.items.map(i => i.profissional_pref).filter(Boolean))]);
const itensFiltrados = computed(() => numerados.value.filter(it =>
  (filtroProf.value === 'Todos' || it.profissional_pref === filtroProf.value) &&
  (filtroJanela.value === 'Todas' || it.janela === filtroJanela.value)
));
const editItem = ref(null);
const showModal = ref(false);
function abrirEdicao(it) { editItem.value = it; showModal.value = true; }
function onModalClose() { showModal.value = false; editItem.value = null; store.load(); }
const showSchedule = ref(false);
const scheduleItem = ref(null);
function abrirAgendar(it) { scheduleItem.value = it; showSchedule.value = true; }
function onScheduleClose() { showSchedule.value = false; scheduleItem.value = null; store.load(); }
const aba = ref('fila');
const eventos = ref([]);
const loadingEv = ref(false);
async function verHistorico() { aba.value = 'historico'; loadingEv.value = true; try { eventos.value = await store.events(); } finally { loadingEv.value = false; } }
function verFila() { aba.value = 'fila'; }
async function mover(it, dir) { await store.reorder(it.id, dir); }
const TIPOS = { added: '➕ Adicionado', edited: '✏️ Editado', removed: '🗑 Removido', scheduled: '✅ Agendado', reordered: '↕ Reordenado' };
function fmtEvData(ts) { return new Date(ts).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
function evDetalhe(ev) {
  const d = ev.detalhe || {};
  const extra = ev.tipo === 'reordered' ? (d.dir === 'up' ? 'subiu' : 'desceu') : ((ev.tipo === 'added' || ev.tipo === 'edited') ? (d.procedimento || '') : '');
  return [d.nome, extra].filter(Boolean).join(' · ');
}
</script>
<template>
  <div class="p-6 overflow-auto w-full">
    <div class="flex items-center gap-3 mb-4">
      <button
        @click="verFila"
        :class="['px-4 py-1 rounded-full text-sm font-medium border', aba === 'fila' ? 'bg-n-brand text-white border-n-brand' : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2']"
      >Fila ({{ store.items.length }})</button>
      <button
        @click="verHistorico"
        :class="['px-4 py-1 rounded-full text-sm font-medium border', aba === 'historico' ? 'bg-n-brand text-white border-n-brand' : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-2']"
      >Histórico</button>
    </div>
    <div v-if="aba === 'fila'">
      <div class="flex items-center gap-4 mb-4">
        <h1 class="text-xl font-semibold">Lista de Espera</h1>
        <select v-model="filtroProf" style="color-scheme: dark" class="px-2 py-1 rounded border border-n-weak bg-transparent text-sm">
          <option v-for="p in profissionais" :key="p" :value="p" style="background-color:#1f2d3d;color:#fff">{{ p }}</option>
        </select>
        <select v-model="filtroJanela" style="color-scheme: dark" class="px-2 py-1 rounded border border-n-weak bg-transparent text-sm">
          <option value="Todas" style="background-color:#1f2d3d;color:#fff">Todas as janelas</option>
          <option value="manha" style="background-color:#1f2d3d;color:#fff">Manhã</option>
          <option value="tarde" style="background-color:#1f2d3d;color:#fff">Tarde</option>
          <option value="qualquer" style="background-color:#1f2d3d;color:#fff">Qualquer</option>
        </select>
      </div>
      <div v-if="store.loading">Carregando…</div>
      <div v-else-if="!store.items.length" class="text-n-slate-11">Ninguém na lista de espera ainda.</div>
      <table v-else class="w-full text-sm">
        <thead><tr class="text-left text-n-slate-11"><th class="py-2 pr-2">#</th><th class="py-2">Paciente</th><th>Procedimento</th><th>Profissional</th><th>Janela</th><th>Observação</th><th>Por</th><th></th></tr></thead>
        <tbody>
          <tr v-for="it in itensFiltrados" :key="it.id" class="border-t border-n-weak">
            <td class="py-2 pr-2 text-n-slate-10">{{ it.posicao }}º</td>
            <td class="py-2">{{ it.nome }}<div class="text-n-slate-10">{{ it.telefone }}</div></td>
            <td>{{ it.procedimento || '—' }}</td>
            <td>{{ it.profissional_pref }}</td>
            <td>{{ JANELA[it.janela] || it.janela }}</td>
            <td>{{ it.observacao || '—' }}</td>
            <td>{{ it.criado_por || '—' }}</td>
            <td class="flex gap-2 items-center py-2">
              <button @click="mover(it, 'up')" :disabled="it.posicao === 1" title="Subir">▲</button>
              <button @click="mover(it, 'down')" :disabled="it.posicao === store.items.length" title="Descer">▼</button>
              <button @click="abrirAgendar(it)" title="Agendar">📅</button>
              <a v-if="it.conversation_chatwoot_id" :href="'/app/accounts/' + accountId + '/conversations/' + it.conversation_chatwoot_id" title="Abrir conversa">💬</a>
              <button @click="abrirEdicao(it)" title="Editar">✏️</button>
              <button @click="store.remove(it.id)" title="Remover">🗑️</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-if="aba === 'historico'">
      <div v-if="loadingEv" class="text-n-slate-11">Carregando…</div>
      <div v-else-if="eventos.length" class="flex flex-col gap-2">
        <div v-for="ev in eventos" :key="ev.id" class="text-sm border-b border-n-weak py-2 flex gap-2 items-baseline">
          <span class="font-medium">{{ TIPOS[ev.tipo] || ev.tipo }}</span>
          <span v-if="evDetalhe(ev)" class="text-n-slate-10">· {{ evDetalhe(ev) }}</span>
          <span class="text-n-slate-11">· por {{ ev.ator_nome || '—' }}</span>
          <span class="text-n-slate-10 ml-auto">{{ fmtEvData(ev.created_at) }}</span>
        </div>
      </div>
      <div v-else class="text-n-slate-11">Nenhum evento ainda.</div>
    </div>
    <WaitlistAddModal v-if="showModal" :editItem="editItem" @close="onModalClose" />
    <WaitlistScheduleModal v-if="showSchedule" :item="scheduleItem" @close="onScheduleClose" />
  </div>
</template>
